import { ReactComponent as ImmediateOrderBadge } from '@/assets/images/order/immediateOrderBadge.svg';
import Accordion from '@/components/Accordion';
import SelectBox from '@/components/SelectBox';
import styled from 'styled-components';
import TextAreaAutoResize from 'react-textarea-autosize';
import { EAlternativeOption, OrderStatusType } from '@/@types/order';
import { GSelectOption } from '@/assets/data/selectOptions';
import RoundedRectangleCheckBox from '@/components/RoundedRectangleCheckBox';
import useOrderImmediateOrderViewModel from '@/hooks/order/useOrderImmediateOrderViewModel';
import { Order } from '@/apis/order/interface';
import PrescriptionCancelReason from '../waitingPayment/PrescriptionCancelReason';
import PrescriptionDenyConfirmModal from '../../modal/PrescriptionDenyConfirmModal';
import { formatComma } from '@/utils/form';

interface Props {
  order: Order;
}

const ImmediateOrder = ({ order }: Props) => {
  const { PrescriptionDenyModal, ...viewModel } =
    useOrderImmediateOrderViewModel({
      order
    });
  const ALTERNATIVE_MANUAL_OPTION = viewModel.alternativeOptions[1];

  return (
    <>
      {viewModel.orderViewStatus === OrderStatusType.ImmediateOrder && (
        <div>
          <ImmediateOrderBadge />
          <Price>
            결제 금액
            <span>{formatComma(order.price)}원</span>
          </Price>
          {order.orderMedicineRequestInfo?.isAlternative && (
            <>
              <MedicineInfoWrapper>
                현재 처방된 약의 재고가 없어서 대체조제 됐어요.
                {order.orderMedicineRequestInfo && (
                  <div>
                    대체조제 약 확인
                    <span>
                      {order.orderMedicineRequestInfo.requestedMedicineName}
                    </span>
                  </div>
                )}
              </MedicineInfoWrapper>
              <AccordionRow>
                <Accordion
                  title={'대체조제 사유'}
                  content={
                    <SelectContent>
                      <SelectBox
                        customStyles={{
                          singleValue: {
                            whiteSpace: 'pre-wrap'
                          }
                        }}
                        options={viewModel.alternativeOptions}
                        defaultValue={viewModel.alternativeOptions[0]}
                        placeholder={'사유 선택'}
                        onChange={value => {
                          viewModel.onChangeAlternativeReason(
                            value as GSelectOption<EAlternativeOption>
                          );
                        }}
                      />
                      {viewModel.alternativeReason?.value ===
                        EAlternativeOption.MANUAL && (
                        <AlternativeInput
                          value={viewModel.alternativeReason.label}
                          onChange={e =>
                            viewModel.onChangeAlternativeReason(
                              ALTERNATIVE_MANUAL_OPTION,
                              e.target.value
                            )
                          }
                          placeholder={'사유를 입력해주세요.'}
                          maxRows={4}
                          maxLength={50}
                          $isMarginTop
                        />
                      )}
                    </SelectContent>
                  }
                  isOpen={viewModel.agreements.isCheckedAlternative}
                />
              </AccordionRow>
            </>
          )}
          <RadioWrapper>
            <RoundedRectangleCheckBox
              isChecked={viewModel.agreements.isCheckedDur}
              onClick={() => viewModel.onPressAgreement('isCheckedDur')}
              content={
                <RadioContent>
                  <span>[필수]&nbsp;</span>
                  DUR에 처방전을 등록했어요.
                </RadioContent>
              }
            />
          </RadioWrapper>
          <ButtonWrapper>
            <Button
              primary
              onClick={viewModel.onSubmitPickupCall}
              disabled={!viewModel.agreements.isCheckedDur}>
              픽업 요청
            </Button>
            <Button
              onClick={() => {
                viewModel.onChangeOrderViewStatus(
                  OrderStatusType.PrescriptionDenied
                );
              }}>
              조제 거절
            </Button>
          </ButtonWrapper>
          <PaymentDescription>
            이미 결제된 건으로, 조제 거절 시 <span>환불이 진행됩니다.</span>
          </PaymentDescription>
        </div>
      )}
      {/* 조제거절 사유 선택 */}
      {viewModel.orderViewStatus === OrderStatusType.PrescriptionDenied && (
        <PrescriptionCancelReason
          isImmediatePayOrder={order.isImmediatePayment}
          orderDenyReasons={viewModel.orderDenyReasons}
          cancelReason={viewModel.cancelReason}
          setCancelReason={viewModel.setCancelReason}
          onSubmitOrderCancelByPharmacy={
            viewModel.onSubmitOrderCancelByPharmacy
          }
          onClickReset={() => {
            viewModel.setCancelReason('');
            viewModel.onChangeOrderViewStatus(OrderStatusType.ImmediateOrder);
          }}
        />
      )}

      {/* 조제 거부 */}
      <PrescriptionDenyModal clickOverlayToClose={false} thin>
        <PrescriptionDenyConfirmModal
          cancelReason={viewModel.cancelReason}
          onCloseModal={viewModel.onChangePrescriptionDenyModal}
          onSubmitPrescriptionDeny={viewModel.onSubmitPrescriptionDeny}
        />
      </PrescriptionDenyModal>
    </>
  );
};

export default ImmediateOrder;

const Price = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 8px 0 20px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI18R};
    color: ${theme.DF_COLOR.GRAY400};
    span {
      ${theme.DF_TYPOGRAPHY.UI24SB};
      color: ${theme.DF_COLOR.GRAY900};
    }
  `};
`;

const MedicineInfoWrapper = styled.div`
  padding: 20px;
  border-radius: 7px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI15R};
    color: ${theme.DF_COLOR.GRAY700};
    background: ${theme.DF_COLOR.GRAY20};

    div {
      display: flex;
      margin-top: 12px;
      justify-content: space-between;
      align-items: center;

      ${theme.DF_TYPOGRAPHY.UI14R};
      color: ${theme.DF_COLOR.GRAY400};

      span {
        ${theme.DF_TYPOGRAPHY.UI16SB};
        color: ${theme.DF_COLOR.PRIMARY700};
      }
    }
  `};
`;

const AccordionRow = styled.div`
  margin-top: 24px;

  ${({ theme }) => `
    border-top: 1px solid ${theme.DF_COLOR.GRAY100};
  `};
`;

const SelectContent = styled.div`
  margin-bottom: 32px;
`;

const AlternativeInput = styled(TextAreaAutoResize)<{ $isMarginTop?: boolean }>`
  border-radius: 4px;
  width: 100%;
  resize: none;
  outline: none;

  ${({ theme, $isMarginTop }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    ${$isMarginTop && 'margin-top: 12px'};
    color: ${theme.DF_COLOR.GRAY700};
    border: 1px solid ${theme.DF_COLOR.GRAY100};
    padding: 14px 16px;

    ::placeholder {
      color: ${theme.DF_COLOR.GRAY200};
    }
  `};
`;

const RadioWrapper = styled.div`
  margin-top: 32px;
`;

const RadioContent = styled.div`
  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    color: ${theme.DF_COLOR.GRAY700};
    span {
      color: ${theme.DF_COLOR.PRIMARY500};
    }
  `};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  gap: 12px;
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 15px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  border: 1px solid;

  ${({ theme, primary }) => `
    ${theme.DF_TYPOGRAPHY.UI17SB};
    color: ${primary ? theme.DF_COLOR.WHITE : theme.DF_COLOR.GRAY600};
    background: ${primary ? theme.DF_COLOR.PRIMARY500 : theme.DF_COLOR.WHITE};
    border-color: ${
      primary ? theme.DF_COLOR.PRIMARY500 : theme.DF_COLOR.GRAY200
    }};
    
    &:hover:not(:disabled) {
      opacity: 0.7;
    }
  
    :disabled {
      background: ${theme.DF_COLOR.GRAY100};
      border-color: ${theme.DF_COLOR.GRAY100};
      cursor: default;
    }
  `};
`;

const PaymentDescription = styled.div`
  margin-top: 12px;
  text-align: center;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI15R};
    color: ${theme.DF_COLOR.GRAY500};

    span {
      color: ${theme.DF_COLOR.PRIMARY800};
    }
  `};
`;
